import { Localized, useLocalization } from "@fluent/react";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { OfferSelect } from "@lending/components/OfferSelect/OfferSelect";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { formatCentsAsDollars } from "@lending/utils/formatters";
import styles from "./LegacyOffers.module.css";
import { usePersonalLoanOffers } from "../hooks/usePersonalLoanOffers";

function LegacyOffers() {
  const { l10n } = useLocalization();
  const {
    application,
    coApplicantFirstName,
    displayAmountCents,
    hasOnlyLowerAmountOffers,
    institution,
    institutionName,
    isLoading,
    isPending,
    offers,
    onSubmit,
    primaryApplicantFirstName,
    shouldLogMissingOffers,
    shouldShowNoOffersError,
    variables,
  } = usePersonalLoanOffers();
  const ctaLabel = l10n.getString("offer-card-submit", null, "Get started");

  useEffect(() => {
    if (shouldLogMissingOffers) {
      Sentry.captureMessage(
        "A user navigated to the personal loan offer screen but did not have an offer. This should not happen."
      );
    }
  }, [shouldLogMissingOffers]);

  if (shouldShowNoOffersError) {
    return <ErrorScreen />;
  }

  return (
    <PageLayout
      isLoading={isLoading || application == null}
      style={{ paddingTop: "var(--space-xxl)" }}
    >
      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className={`${styles.title} margin--bottom--xs`}>
              {coApplicantFirstName
                ? l10n.getString(
                    "offers-heading-joint",
                    {
                      firstName: primaryApplicantFirstName,
                      secondName: coApplicantFirstName,
                    },
                    `🎉 Good news, ${primaryApplicantFirstName} and ${coApplicantFirstName}! 🎉`
                  )
                : l10n.getString(
                    "offers-heading",
                    { firstName: primaryApplicantFirstName },
                    `🎉 Good news, ${primaryApplicantFirstName}! 🎉`
                  )}
            </h1>
            <div className={`fontColor--heading fontFamily--heading ${styles.offerTitle}`}>
              {hasOnlyLowerAmountOffers
                ? l10n.getString(
                    "offers-subheading-lower-than-requested",
                    null,
                    "We can offer you a loan for up to"
                  )
                : l10n.getString(
                    "offers-subheading",
                    null,
                    "You're prequalified for a loan of"
                  )}{" "}
              <span
                className={`fontColor--heading fontFamily--default fontWeight--bold ${styles.maxOffer}`}
              >
                {formatCentsAsDollars(displayAmountCents)}
              </span>
            </div>
          </div>
          <div className={styles.offerDetails}>
            {offers.length > 0 && (
              <div className={styles.offersRow}>
                {offers.slice(0, 3).map((offer) => (
                  <OfferSelect
                    key={offer.id}
                    chipLabel={l10n.getString(
                      "offer-card-chip-label",
                      { termMonths: offer.term_months },
                      `${offer.term_months} months`
                    )}
                    kind="loan"
                    monthly_payment={offer.monthly_payment}
                    bulletPoints={[
                      l10n.getString(
                        "offer-card-line-1",
                        { aprBasis: offer.apr_basis / 100 },
                        `${(offer.apr_basis / 100).toFixed(2)}% APR`
                      ),
                      l10n.getString("offer-card-line-2", null, "No additional fees"),
                      l10n.getString("offer-card-line-3", null, "Immediate funding"),
                    ]}
                    infoLinkText={l10n.getString(
                      "offer-card-info-link-text",
                      null,
                      "Info & disclaimer"
                    )}
                    infoLinkUrl={
                      institution.lending_personal_loan_offer_info_and_disclaimer_url ||
                      institution.lending_offer_info_and_disclaimer_url
                    }
                    onSelect={() => onSubmit(offer)}
                    isPending={isPending && variables?.body?.product_offer === offer.id}
                    isDisabled={isPending && variables?.body?.product_offer !== offer.id}
                  />
                ))}
              </div>
            )}
          </div>
          <div className={styles.footer}>
            <div className="fontSize--s fontWeight--thin">
              <Localized
                id="offers-consent-disclaimer-personal"
                vars={{ ctaLabel, institutionName }}
              >
                {`By clicking "${ctaLabel}" above, you consent to authorize ${institutionName} to obtain and use Consumer Reports about you to review your application, to review the products or services you maintain, and to analyze your financial needs and to market other products and services to you.`}
              </Localized>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export { LegacyOffers };
